import React, { useState, useEffect } from "react";
import {
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Snackbar,
  Dialog,
  Tooltip,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { Task, Delete } from "@mui/icons-material";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";

// Material Dashboard 2 React example components
import DataTable from "shared/components/Tables/DataTable";

// Custom components
import CustomModal from "shared/components/MDmodal/CustomModal";
import {
  getAllUsersService,
  putUserService,
  updateUserService,
} from "modules/usuario/services/userService";
import FormJobOrder from "../Form/FormJobOrder";

function TableJobOrder({ refreshFlag }) {
  const [usuarios, setUsuarios] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Función para mostrar notificaciones
  const showNotification = (message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const fetchUsuarios = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getAllUsersService();
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;

      setUsuarios(parsedData || []);
    } catch (err) {
      console.error("❌ Error al obtener usuarios:", err);
      setError("Error al cargar usuarios");
      showNotification("Error al cargar usuarios", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, [refreshFlag]);

  const handleEditar = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setOpenModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setLoading(true);
      await putUserService(userToDelete);
      await fetchUsuarios();
      showNotification("Usuario eliminado correctamente", "success");
    } catch (err) {
      console.error("Error al eliminar usuario:", err);
      const errorMessage = err.message.includes("token")
        ? "Su sesión ha expirado. Por favor inicie sesión nuevamente."
        : err.message;
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
      setUserToDelete(null);
    }
  };

  const handleActualizar = async (datos) => {
    try {
      setLoading(true);
      await updateUserService(usuarioSeleccionado.id, {
        username: datos.username,
        email: datos.email,
        roleId: datos.roleId,
        status: datos.status,
      });
      await fetchUsuarios();
      setOpenModal(false);
      showNotification("Usuario actualizado correctamente", "success");
    } catch (err) {
      console.error("Error al actualizar usuario:", err);
      setError(err.response?.data?.message || "Error al actualizar usuario");
      showNotification(err.response?.data?.message || "Error al actualizar usuario", "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { Header: "Acciones", accessor: "acciones", align: "center", width: "10%" },
    { Header: "ID", accessor: "id", width: "10%" },
    { Header: "Usuario", accessor: "usuario", width: "30%" },
    { Header: "Correo", accessor: "email", width: "40%" },
    { Header: "Rol", accessor: "roleId", width: "10%" },
    { Header: "Estado", accessor: "estado", width: "10%" },
  ];

  const rows = usuarios.map((usuario) => ({
    id: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {usuario.id}
      </MDTypography>
    ),
    usuario: (
      <MDBox display="flex" alignItems="center" lineHeight={1}>
        <Avatar src={usuario.avatar} alt={usuario.username} sx={{ width: 36, height: 36 }} />
        <MDBox ml={2}>
          <MDTypography variant="button" fontWeight="medium">
            {usuario.username || "Sin nombre"}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            {usuario.role?.name || ""}
          </MDTypography>
        </MDBox>
      </MDBox>
    ),
    email: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {usuario.email || "Sin correo"}
      </MDTypography>
    ),
    roleId: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {usuario.roleId || "Sin rol"}
      </MDTypography>
    ),
    estado: (
      <MDTypography
        variant="caption"
        color={usuario.status === 1 ? "success" : "error"}
        fontWeight="medium"
      >
        {usuario.status === 1 ? "Activo" : "Inactivo"}
      </MDTypography>
    ),
    acciones: (
      <MDBox display="flex" justifyContent="center">
        <Tooltip title="Resolver agendamiento">
          <IconButton color="info" onClick={() => handleEditar(usuario)}>
            <Task />
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  }));

  return (
    <>
      <MDBox
        mx={-1}
        mt={-1}
        py={2}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
      >
        <MDTypography variant="h6" color="white">
          Gestión de Ordenes de Trabajo
        </MDTypography>
      </MDBox>
      <MDBox pt={1} px={-2}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" p={3}>
            <CircularProgress color="info" />
          </MDBox>
        ) : error ? (
          <MDBox p={2}>
            <Alert severity="error">{error}</Alert>
          </MDBox>
        ) : (
          <DataTable
            table={{ columns, rows }}
            isSorted
            entriesPlEntries
            noEndBoerPage={false}
            showTotarder
          />
        )}
      </MDBox>

      {/* Notificación Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={openDeleteDialog}
        onClose={() => !loading && setOpenDeleteDialog(false)}
        PaperProps={{
          sx: {
            minWidth: "400px",
            borderRadius: "12px",
            padding: "16px",
          },
        }}
      >
        <MDBox display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: "error.light", mr: 2 }}>
            <Delete color="error" />
          </Avatar>
          <MDTypography variant="h6" fontWeight="medium">
            Confirmar eliminación
          </MDTypography>
        </MDBox>

        <DialogContent>
          <MDTypography variant="body2">Esta acción eliminará al usuario del sistema.</MDTypography>
          <MDTypography variant="body2" fontWeight="bold" mt={1}>
            ¿Deseas continuar?
          </MDTypography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <MDButton
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            color="error"
            disabled={loading}
            sx={{ minWidth: "120px" }}
          >
            Cancelar
          </MDButton>
          <MDButton
            onClick={confirmDelete}
            color="error"
            variant="gradient"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ minWidth: "120px" }}
          >
            {loading ? "Eliminando..." : "Confirmar"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Modal para editar usuario */}
      <CustomModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setError(null);
        }}
        title={`Editar Usuario - ${usuarioSeleccionado?.username || ""}`}
      >
        {error && (
          <MDBox mb={2}>
            <Alert severity="error">{error}</Alert>
          </MDBox>
        )}
        {usuarioSeleccionado && (
          <FormJobOrder
            initialData={{
              username: usuarioSeleccionado?.username || "",
              email: usuarioSeleccionado?.email || "",
              roleId: usuarioSeleccionado?.roleId?.toString() || "",
              status: usuarioSeleccionado?.status ?? 1,
            }}
            onSubmit={handleActualizar}
            isEditMode={true}
            loading={loading}
          />
        )}
      </CustomModal>
    </>
  );
}

TableJobOrder.propTypes = {
  refreshFlag: PropTypes.bool,
};
export default TableJobOrder;
